import React, { useCallback, useEffect, useState } from 'react';
import { View, ScrollView, ActivityIndicator, RefreshControl } from 'react-native';
import { observer } from 'mobx-react';

import Modal from './shared/Modal';
import Text from './shared/Text';
import Button from './shared/Button';
import IconButton from './shared/IconButton';
import { TextInputLabel } from './shared/TextInput';
import { InfoIcon } from './common/Info';
import { confirm } from './shared/Alert';
import WarriorLoginForm from './common/WarriorLoginForm';
import { IS_WEB } from '../lib/appConstants';

import heroStore from '../stores/hero';
import appStore from '../stores/app';
import clansStore from '../stores/clans';
import warriorInfoModalStore from './common/WarriorInfoModal/store';

import {
  createClan as createClanApi,
  acceptClanInvite,
  declineClanInvite,
  inviteToClan as inviteToClanApi,
  leaveClan as leaveClanApi,
  kickFromClan,
  disbandClan as disbandClanApi,
  declareWar as declareWarApi,
  joinWar as joinWarApi,
  startWar as startWarApi,
  cancelWar as cancelWarApi,
} from '../lib/apiCalls';
import { fetchClanLeaders, ClanLeader } from '../lib/leaderboardCache';
import { CLAN } from '../lib/config';
import { t } from '../utils/appUtils';
import {
  BASE_COLOR,
  GOLDEN100,
  GREEN,
  RED,
  MARGIN_5,
  MARGIN_10,
  MARGIN_15,
  pOrT,
  BUTTON_HEIGHT,
  GOLDEN,
} from './styleConstants';

type Props = { onHide: () => void };

const Divider = () => (
  <View
    style={{ height: 1, backgroundColor: GOLDEN, marginVertical: MARGIN_10, opacity: 0.5 }}
  />
);

const SectionTitle = ({ children }: { children: any }) => (
  <Text bold style={{ color: GOLDEN, marginBottom: MARGIN_5 }}>
    {children}
  </Text>
);

function CreateClanForm() {
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');

  const onCreate = async () => {
    if (name.trim().length < CLAN.NAME_MIN || name.trim().length > CLAN.NAME_MAX) {
      appStore.showMessage({ type: 'error', text: t('clan.errors.nameLength') });
      return;
    }
    if (tag.trim().length < CLAN.TAG_MIN || tag.trim().length > CLAN.TAG_MAX) {
      appStore.showMessage({ type: 'error', text: t('clan.errors.tagLength') });
      return;
    }
    if ((heroStore.hero.money || 0) < CLAN.CREATION_COST) {
      appStore.showMessage({ type: 'error', text: t('clan.errors.notEnoughMoney') });
      return;
    }
    try {
      await createClanApi(name.trim(), tag.trim());
      appStore.showMessage({ type: 'success', text: t('clan.created') });
      setName('');
      setTag('');
    } catch (e) {
      // The request interceptor already surfaced the server error.
    }
  };

  return (
    <View>
      <SectionTitle>{t('clan.createHeading')}</SectionTitle>
      <Text style={{ marginBottom: MARGIN_5 }}>
        {t('clan.createCost', { cost: CLAN.CREATION_COST })}
      </Text>
      <TextInputLabel
        width={pOrT(200, 240)}
        placeholder={t('clan.namePlaceholder')}
        value={name}
        onChangeText={setName}
        maxLength={CLAN.NAME_MAX}
      />
      <TextInputLabel
        width={pOrT(200, 240)}
        placeholder={t('clan.tagPlaceholder')}
        value={tag}
        onChangeText={setTag}
        maxLength={CLAN.TAG_MAX}
        autoCapitalize="characters"
      />
      <Button onPress={onCreate}>{t('clan.createButton')}</Button>
    </View>
  );
}

const InvitesList = observer(function InvitesList() {
  const { invites } = clansStore;

  const onAccept = async (clanId: string) => {
    try {
      await acceptClanInvite(clanId);
    } catch (e) {
      // interceptor surfaced the error
    }
  };
  const onDecline = async (clanId: string) => {
    try {
      await declineClanInvite(clanId);
    } catch (e) {
      // ignore
    }
  };

  return (
    <View>
      <SectionTitle>{t('clan.invitesHeading')}</SectionTitle>
      {invites.length === 0 ? (
        <Text>{t('clan.noInvites')}</Text>
      ) : (
        invites.map((inv) => (
          <View
            key={inv.clanId}
            style={{ flexDirection: 'row', alignItems: 'center', marginTop: MARGIN_5 }}
          >
            <Text style={{ flex: 1 }}>
              {t('clan.invitedBy', { clan: inv.clanName, login: inv.fromLogin })}
            </Text>
            <Button
              sizeObj={{ width: 74, height: 26 }}
              onPress={() => onAccept(inv.clanId)}
              style={{ marginRight: MARGIN_5 }}
            >
              {t('clan.accept')}
            </Button>
            <Button
              variant="red"
              sizeObj={{ width: 74, height: 26 }}
              onPress={() => onDecline(inv.clanId)}
            >
              {t('clan.decline')}
            </Button>
          </View>
        ))
      )}
    </View>
  );
});

function InviteMemberForm() {
  const [fields, setFields] = useState<{ login: string }>({ login: '' });
  const [selectedWarrior, setSelectedWarrior] = useState<any>(null);

  const onInvite = async () => {
    if (!selectedWarrior) {
      appStore.showMessage({ type: 'error', text: t('clan.errors.targetNotFound') });
      return;
    }
    if (selectedWarrior.clanId) {
      appStore.showMessage({ type: 'error', text: t('clan.errors.targetInClan') });
      return;
    }
    try {
      await inviteToClanApi(selectedWarrior.id);
      appStore.showMessage({ type: 'success', text: t('clan.invited') });
      setFields({ login: '' });
      setSelectedWarrior(null);
    } catch (e) {
      // interceptor surfaced the error
    }
  };

  return (
    <View>
      <SectionTitle>{t('clan.inviteHeading')}</SectionTitle>
      <View style={{ width: 215 }}>
        <WarriorLoginForm
          item={{ name: 'login', label: t('clan.invitePlaceholder'), type: 'TEXT' }}
          fields={fields}
          setFields={setFields}
          selectedWarrior={selectedWarrior}
          setSelectedWarrior={setSelectedWarrior}
        />
      </View>
      <Button sizeObj={{ width: 160, height: BUTTON_HEIGHT }} onPress={onInvite}>{t('clan.invite')}</Button>
    </View>
  );
}

const WarPanel = observer(function WarPanel() {
  const { clan, currentWar, isLeader } = clansStore;
  const [enemyName, setEnemyName] = useState('');

  if (!clan) return null;

  const heroJoined = Boolean(
    currentWar && currentWar.joined && currentWar.joined[heroStore.hero.id],
  );
  const joinedCount = currentWar && currentWar.joined ? Object.keys(currentWar.joined).length : 0;

  const wrap = (fn: () => Promise<any>) => async () => {
    try {
      await fn();
    } catch (e) {
      // interceptor surfaced the error
    }
  };

  const onDeclare = async () => {
    if (!enemyName.trim()) {
      appStore.showMessage({ type: 'error', text: t('clan.errors.clanNotFound') });
      return;
    }
    try {
      await declareWarApi(enemyName.trim());
      appStore.showMessage({
        type: 'success',
        text: t('clan.war.declared', { clan: enemyName.trim() }),
      });
      setEnemyName('');
    } catch (e) {
      // interceptor surfaced the error
    }
  };

  return (
    <View>
      <SectionTitle>{t('clan.war.heading')}</SectionTitle>

      {!currentWar ? (
        isLeader ? (
          <View>
            <TextInputLabel
              width={pOrT(200, 240)}
              placeholder={t('clan.war.enemyPlaceholder')}
              value={enemyName}
              onChangeText={setEnemyName}
            />
            <Button sizeObj={{ width: 160, height: BUTTON_HEIGHT }} variant="red" onPress={onDeclare}>
              {t('clan.war.declare')}
            </Button>
          </View>
        ) : (
          <Text>{t('clan.war.noMembers')}</Text>
        )
      ) : (
        <View>
          <Text bold style={{ color: GOLDEN }}>
            {t('clan.war.vs', { clan: clan.name, enemy: currentWar.enemyClanName })}
          </Text>
          {currentWar.status === 'lobby' ? (
            <View style={{ marginTop: MARGIN_5 }}>
              <Text>{t('clan.war.lobby')}</Text>
              <Text style={{ marginTop: MARGIN_5 }}>
                {t('clan.war.joinedCount', { count: joinedCount })}
              </Text>
              <View style={{ flexDirection: 'row', marginTop: MARGIN_5, flexWrap: 'wrap' }}>
                {!heroJoined ? (
                  <Button
                    onPress={wrap(joinWarApi)}
                    style={{ marginRight: MARGIN_5, marginBottom: MARGIN_5 }}
                  >
                    {t('clan.war.joinBattle')}
                  </Button>
                ) : (
                  <Text style={{ color: GREEN, marginRight: MARGIN_10 }}>
                    {t('clan.war.joined')}
                  </Text>
                )}
                {isLeader ? (
                  <>
                    {currentWar.team === 1 ? (
                      <Button
                        onPress={wrap(startWarApi)}
                        style={{ marginRight: MARGIN_5, marginBottom: MARGIN_5 }}
                      >
                        {t('clan.war.start')}
                      </Button>
                    ) : null}
                    <Button variant="red" onPress={wrap(cancelWarApi)}>
                      {t('clan.war.cancel')}
                    </Button>
                  </>
                ) : null}
              </View>
            </View>
          ) : (
            <Text style={{ color: RED, marginTop: MARGIN_5 }}>{t('clan.war.fighting')}</Text>
          )}
        </View>
      )}
    </View>
  );
});

function TopClans({ clans }: { clans: ClanLeader[] | null }) {
  return (
    <View>
      <SectionTitle>{t('clan.topHeading')}</SectionTitle>
      <View
        style={{
          flexDirection: 'row',
          borderBottomWidth: 1,
          borderBottomColor: GOLDEN,
          paddingBottom: 4,
        }}
      >
        <Text bold style={{ flex: 0.4 }}>
          {t('clan.topColumns.rank')}
        </Text>
        <Text bold style={{ flex: 2 }}>
          {t('clan.topColumns.clan')}
        </Text>
        <Text bold style={{ flex: 1 }}>
          {t('clan.topColumns.members')}
        </Text>
        <Text bold style={{ flex: 1 }}>
          {t('clan.topColumns.warWins')}
        </Text>
      </View>
      {!clans ? (
        <ActivityIndicator color={BASE_COLOR} style={{ marginTop: MARGIN_5 }} />
      ) : clans.length === 0 ? (
        <Text style={{ marginTop: MARGIN_5 }}>—</Text>
      ) : (
        clans.map((c) => (
          <View key={c.id} style={{ flexDirection: 'row', marginTop: 4 }}>
            <Text style={{ flex: 0.4 }}>{c.rank}</Text>
            <Text style={{ flex: 2 }}>{`${c.name} [${c.tag}]`}</Text>
            <Text style={{ flex: 1 }}>{c.members}</Text>
            <Text style={{ flex: 1 }}>{c.warWins}</Text>
          </View>
        ))
      )}
    </View>
  );
}

const ClanView = observer(function ClanView() {
  const { clan, isLeader, members, memberCount } = clansStore;
  if (!clan) return null;

  const onLeave = () =>
    confirm(t('clan.confirmLeave'), async () => {
      try {
        await leaveClanApi();
      } catch (e) {
        // interceptor surfaced the error
      }
    });

  const onDisband = () =>
    confirm(t('clan.confirmDisband'), async () => {
      try {
        await disbandClanApi();
      } catch (e) {
        // interceptor surfaced the error
      }
    });

  const onKick = (id: string, login: string) =>
    confirm(t('clan.confirmKick', { login }), async () => {
      try {
        await kickFromClan(id);
      } catch (e) {
        // interceptor surfaced the error
      }
    });

  return (
    <View>
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text bold style={{ fontSize: pOrT(14, 16), color: GOLDEN, marginRight: MARGIN_10 }}>
          {`${clan.name} [${clan.tag}]`}
        </Text>
      </View>
      <View style={{ flexDirection: 'row', marginTop: MARGIN_5 }}>
        <Text style={{ marginRight: MARGIN_15 }}>
          {`${t('clan.warWins')}: ${clan.warWins || 0}`}
        </Text>
        <Text>{`${t('clan.warLosses')}: ${clan.warLosses || 0}`}</Text>
      </View>

      <Divider />

      {/* Members */}
      <SectionTitle>
        {t('clan.membersHeading', { count: memberCount, max: CLAN.MAX_MEMBERS })}
      </SectionTitle>
      {members.map((m) => (
        <View key={m.id} style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
            <Text style={{ marginRight: MARGIN_5 }}>
              {m.login}
              {m.id === clan.leaderId ? ` (${t('clan.leader')})` : ''}
            </Text>
            <InfoIcon
              style={{}}
              iconStyle={{ width: pOrT(16, 18), height: pOrT(16, 18) }}
              onPress={() => warriorInfoModalStore.toggle(m.id, true)}
            />
          </View>
          {isLeader && m.id !== clan.leaderId ? (
            <Button
              variant="red"
              sizeObj={{ width: 66, height: 24 }}
              onPress={() => onKick(m.id, m.login)}
            >
              {t('clan.kick')}
            </Button>
          ) : null}
        </View>
      ))}

      <Divider />

      {/* Leader / member controls */}
      {isLeader ? <InviteMemberForm /> : null}

      <Divider />

      <WarPanel />

      <Divider />

      {/* Leave / disband */}
      <View style={{ flexDirection: 'row' }}>
        {isLeader ? (
          <Button  sizeObj={{ width: 160, height: BUTTON_HEIGHT }} variant="red" onPress={onDisband}>
            {t('clan.disband')}
          </Button>
        ) : (
          <Button sizeObj={{ width: 160, height: BUTTON_HEIGHT }} variant="red" onPress={onLeave}>
            {t('clan.leave')}
          </Button>
        )}
      </View>
    </View>
  );
});

export default observer(function ClanModal({ onHide }: Props) {
  const { clan } = clansStore;
  const [topClans, setTopClans] = useState<ClanLeader[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadTopClans = useCallback((force?: boolean) => {
    return fetchClanLeaders(force)
      .then(setTopClans)
      .catch(() => {});
  }, []);

  // Clear + re-sync the clan store and load the ranking when the modal opens.
  useEffect(() => {
    clansStore.refresh();
    loadTopClans();
  }, [loadTopClans]);

  // Re-sync live clan/invite data and force-refetch the ranking.
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    clansStore.refresh();
    await loadTopClans(true);
    setRefreshing(false);
  }, [loadTopClans]);

  return (
    <Modal
      title={t('clan.title')}
      onHide={onHide}
      size={{ width: pOrT(520, 640), height: pOrT(360, 620) }}
      warriorListener
    >
      <View
        style={{
          marginLeft: 30,
          marginRight: 30,
          marginTop: pOrT(30, 50),
          marginBottom: pOrT(25, 25),
          flex: 1,
        }}
      >
        {IS_WEB ? (
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: -30 }}>
            <IconButton
              onPress={onRefresh}
              size={14}
              name="refresh"
              title={t('refresh')}
              source={require('../assets/images/refresh.png')}
              iconStyle={{ width: 24, height: 24 }}
            />
          </View>
        ) : null}
        <ScrollView
          refreshControl={
            IS_WEB ? undefined : (
              <RefreshControl
                tintColor={BASE_COLOR}
                refreshing={refreshing}
                onRefresh={onRefresh}
              />
            )
          }
        >
          {clan ? (
            <ClanView />
          ) : (
            <View>
              <CreateClanForm />
              <Divider />
              <InvitesList />
            </View>
          )}
          <Divider />
          <TopClans clans={topClans} />
        </ScrollView>
      </View>
    </Modal>
  );
});
